import { useState, useEffect } from 'react';
import {
  Button, PasswordInput, Text, TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useForm } from '@mantine/form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { notifications } from '@mantine/notifications';
import validate from '@/helpers/validations';
import { getMutationResult } from '@/helpers/query';
import { useUserLoginMutation } from '@/services/api';
import { updateUser, updateUserRole } from '@/data/features/user/user';
import routes from '@/routes';
import LoginImage from '@/assets/images/loginImage.svg?react'; // eslint-disable-line import/no-unresolved
import LogoBig from '@/assets/images/logoBig.svg?react'; // eslint-disable-line import/no-unresolved
import classes from './login.module.css';
import i18n from '@/services/i18n/i18n';

const Login = () => {
  const { t } = useTranslation();
  const [apiLogin] = useUserLoginMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showExpiredTime, setShowExpiredTime] = useState(false);

  const required = t('globals.validations.required');
  const email = t('globals.validations.email');

  const formValidations = {
    email: (value) => validate(value, { required, email }),
    password: (value) => validate(value, { required }),
  };

  const initialValues = {
    email: '',
    password: '',
  };

  const form = useForm({
    mode: 'uncontrolled',
    initialValues,
    validate: formValidations,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('exit') === '1') {
      setShowExpiredTime(true);
    }
  }, []);

  const { onSubmit } = form;

  const handleSubmit = async (values) => {
    setLoading(true);

    const loginData = { ...values };

    // eslint-disable-next-line no-unused-vars
    const { data, isError } = await getMutationResult(loginData, apiLogin);

    if (!isError && data?.data?.role) {
      const { role, name, config } = data.data;
      dispatch(updateUser({ config }));
      const translation = config?.translation || {};
      const { accessToken, refreshToken, refreshTokenId } = data;

      const lng = 'he';
      const ns = 'translation';
      const key = `locales_${lng}-${ns}`;
      const currentTranslation = JSON.parse(localStorage.getItem(key));

      const updatedTranslation = {
        ...currentTranslation,
        ...translation,
      };

      i18n.addResourceBundle(lng, ns, updatedTranslation, true, true);

      localStorage.setItem(key, JSON.stringify(updatedTranslation));

      dispatch(updateUserRole({
        role,
        accessToken,
        name,
        refreshToken,
        refreshTokenId,
      }));

      navigate(routes.main.path);
    } else {
      setLoading(false);
      if (data?.status === 401) {
        notifications.show({
          title: t('globals.unauthorized.title'),
          message: t('globals.unauthorized.message'),
        });
      } else {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
      }
    }
  };

  const actions = (
    <Button w={'9rem'} onClick={onSubmit((values) => handleSubmit(values))} loading={loading}>
      {t('pages.login.action')}
    </Button>
  );

  return (
    <div className={classes.loginWrapper}>
      {/* Right Side - Page Content */}
      <div className={classes.rightSide}>
        {/* Logo and title at the top */}
        <div className={classes.logoContainer}>
          <LogoBig />
        </div>

        <div className={classes.formContent}>
          {showExpiredTime && <div className={classes.sessionExpired}>
            {t('pages.login.sessionExpired')}
          </div>}
          <div className={classes.formHeader}>
            <p className={classes.formTitle}>{t('pages.login.title')}</p>
            {/* <p className={classes.formSubtitle}>{t('pages.login.subTitle')}</p> */}
          </div>
          <form>
            <div className={classes.login}>
              <TextInput
                label={t('pages.login.inputs.email')}
                key={form.key('email')}
                {...form.getInputProps('email')}
              />

              <PasswordInput
                label={t('pages.login.inputs.password')}
                key={form.key('password')}
                {...form.getInputProps('password')}
              />
              {actions}
            </div>
          </form>
        </div>
      </div>
      {/* Left Side - Image */}
      <div className={classes.leftSide}>
        <LoginImage />
      </div>
      {/* Footer */}
      <footer className={classes.footer}>
        <a
          href="https://www.creditpro.co.il/%D7%9E%D7%93%D7%99%D7%A0%D7%99%D7%95%D7%AA-%D7%94%D7%A4%D7%A8%D7%98%D7%99%D7%95%D7%AA"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text>
            {t('globals.privacyPolicy')}
          </Text>
        </a>
        <Text> | </Text>
        <a
          href="https://www.creditpro.co.il/%D7%AA%D7%A0%D7%90%D7%99-%D7%A9%D7%99%D7%9E%D7%95%D7%A9-%D7%91%D7%90%D7%AA%D7%A8"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text>
            {t('globals.termsOfUse')}
          </Text>
        </a>
      </footer>

    </div>
  );
};

export default Login;
